import React, { type JSX } from 'react';
import { CustomTable, CustomTransComponent } from '../../components';
import { Box, Chip, IconButton, Paper, Stack, Switch } from '@mui/material';
import { Header } from '@fiji/common/src/types';
import LooksOne from '@mui/icons-material/LooksOne';
import { useGetAllLoadsQuery } from '@fiji/common/src/features/deviceManagement/deviceApi';
import { useNavigate, useParams } from 'react-router-dom';
import { useTypedSelector } from '@fiji/common/src/app/store';
import { useTheme } from '@mui/material/styles';
import {
    setLoadModal,
    setPayload,
    setSearchKey,
    resetFilters,
    resetAllFilters,
    selectLoadPayload,
    selectSearchKey,
} from '@fiji/common/src/features/Loads/loadSlice';
import ChevronRight from '@mui/icons-material/ChevronRight';
import { useTranslation } from 'react-i18next';
import { useDebounce, useIsMount } from '@fiji/common/src/hooks';
import { useDispatch } from 'react-redux';
import Search from '@mui/icons-material/Search';
import { usePayloadActions } from '@fiji/common/src/hooks/usePayloadActions';
import { useNewCustomTranslations, useRBAC } from 'hooks';
import { selectCurrentPermission } from '@fiji/common/src/features/profile/profileSlice';
import { InputWithCustomPlaceholder } from 'components/InputWithCustomPlaceholder';

const getModifiedPayload = (filters: any): any => {
    const clonefilters = JSON?.parse(JSON.stringify(filters ?? {}));
    if (clonefilters?.isLoadEnabled) {
        if (clonefilters?.isLoadEnabled?.length > 1) {
            delete clonefilters['isLoadEnabled'];
        } else {
            clonefilters['isLoadEnabled'] = clonefilters['isLoadEnabled'][0];
        }
    }
    return clonefilters;
};

const buildSwitchSx = (theme: any): any => {
    const primary = theme?.palette?.primary;
    return {
        '& .MuiSwitch-switchBase.Mui-checked': {
            color: primary?.main,
            '& + .MuiSwitch-track': {
                backgroundColor: primary?.main,
            },
        },
        '& .MuiSwitch-switchBase.Mui-checked.Mui-disabled': {
            color: primary?.[50],
            '& + .MuiSwitch-track': {
                backgroundColor: primary?.[200],
            },
        },
    };
};

const ActionCell = (
    data: any,
    currentRealm: any,
    deviceId: any,
    navigate: any,
    isRampShedInProgress: boolean
): JSX.Element => (
    <IconButton
        id="details"
        onClick={(): void => {
            if (isRampShedInProgress) return;
            navigate(`/${currentRealm}/loadDetails/${deviceId}/${data?.loadId}`);
        }}
        disabled={isRampShedInProgress}
        sx={{
            cursor: isRampShedInProgress ? 'not-allowed' : 'pointer',
            '&.Mui-disabled': {
                cursor: 'not-allowed',
                pointerEvents: 'auto',
            },
        }}
    >
        <ChevronRight />
    </IconButton>
);

const getLoadNumber = (data: any): JSX.Element => (
    <Box className="f-14 fw-600 bg-content text-white width-18 border-radius-4 flex-center-align">
        {parseInt(data?.['loadNumber'] ?? 0) + 1}
    </Box>
);

const getIsLoadEnabled = (
    data: any,
    handleSwitch: any,
    loads: any,
    theme: any,
    canUpdateLoads: boolean,
    isRampShedInProgress: boolean
): JSX.Element => (
    <Switch
        onClick={(): void => handleSwitch(data)}
        checked={data?.isLoadEnabled}
        disabled={!data?.isLoadEnabledInMap || loads?.data?.offlineDevice || !canUpdateLoads || isRampShedInProgress}
        color="primary"
        sx={buildSwitchSx(theme)}
    />
);

const buildLoadFiltersPayload = (filters: any): any =>
    Object.keys(filters ?? {})?.length ? { filters: getModifiedPayload(filters) } : { filters: null };

export const Loads = ({ isRampShedInProgress = false }: { isRampShedInProgress?: boolean }): JSX.Element => {
    // React.useEffect(() => {
    //     console.log('Loads prop changed:', isRampShedInProgress);
    // }, [isRampShedInProgress]);
    const theme: any = useTheme();
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const isMount = useIsMount();
    const navigate = useNavigate();
    const { deviceId } = useParams();
    const tableRef: any = React.useRef(null);
    const searchKey = useTypedSelector(selectSearchKey);
    const payload = useTypedSelector(selectLoadPayload);
    const currentRealm = useTypedSelector((state) => state.common.selectedRealm);
    const [, debouncedValue] = useDebounce(undefined, undefined, searchKey);
    const [cleanPayload] = usePayloadActions();

    const permissions = useTypedSelector(selectCurrentPermission);
    const { hasPermission } = useRBAC(permissions);
    const canUpdateLoads = hasPermission('device-loads_edit-devices');
    useNewCustomTranslations('HEALTH_STATUS');

    const filters = payload?.filters;

    const {
        data: loads,
        isLoading,
        isFetching,
    } = useGetAllLoadsQuery(
        {
            deviceId,
            body: cleanPayload({
                ...payload,
                ...buildLoadFiltersPayload(filters),
            }),
        },
        { refetchOnMountOrArgChange: true, skip: !deviceId }
    );

    const loadsData = loads?.data;

    const handlePageChange = (page: any, size: any): void => {
        dispatch(setPayload({ page: page, size: size }));
    };

    const handleFilterChange = (nextFilters: any, sortData?: any): void => {
        if (JSON.stringify(nextFilters) !== JSON.stringify(payload.filters)) {
            dispatch(
                setPayload({
                    ...payload,
                    filters: nextFilters,
                })
            );
        }
        if (sortData && Object.keys(sortData)?.length) {
            dispatch(
                setPayload({
                    ...payload,
                    sort: sortData,
                })
            );
        }
    };

    const handleSwitch = (data: any): void => {
        if (data?.isLoadEnabled) {
            dispatch(
                setLoadModal({
                    isOpen: true,
                    command: 'TURN_OFF',
                    details: data,
                })
            );
            return;
        }
        dispatch(
            setLoadModal({
                isOpen: true,
                command: 'TURN_ON',
                details: data,
            })
        );
    };
    React.useEffect(() => {
        if (!isMount) {
            dispatch(
                setPayload({
                    ...payload,
                    searchKey: debouncedValue,
                })
            );
        }
    }, [debouncedValue]);

    React.useEffect(
        () => () => {
            dispatch(resetAllFilters());
        },
        []
    );

    const tableRows: Header[] = [
        {
            header: `${t('HEALTH_STATUS:LOAD')}  #`,
            isSortable: true,
            width: '15%',
            accessor: 'loadNumber',
            cell: (data: any): JSX.Element => getLoadNumber(data),
        },
        {
            header: t('COMMON:NAME'),
            width: '20%',
            isSortable: true,
            accessor: 'loadName',
        },
        {
            header: t('COMMON:STATE'),
            width: '15%',
            isSortable: true,
            accessor: 'isLoadEnabled',
            cell: (data: any): JSX.Element =>
                getIsLoadEnabled(data, handleSwitch, loads, theme, canUpdateLoads, isRampShedInProgress),
            isFilterable: true,
            filterOptions: [
                { id: 'true', label: t('COMMON:ACTIVE') },
                { id: 'false', label: t('COMMON:DISABLED') },
            ],
        },
        {
            width: '45%',
            header: (<CustomTransComponent translationKey={'ALARM_DETAILS:DESCRIPTION_TITLE'} />) as any,
            accessor: 'loadDescription',
        },
        {
            header: '',
            width: '5%',
            cell: (data: any) => ActionCell(data, currentRealm, deviceId, navigate, isRampShedInProgress),
        },
    ];

    const handleChipDelete = (key: any): void => {
        tableRef?.current?.resetFilters(key === 'groupId' ? 'groupPath' : key, true);
        dispatch(resetFilters(key));
    };

    const getChipName = (key: string): any => {
        if (key === 'isLoadEnabled') {
            return t('COMMON:STATE');
        }
    };

    return (
        <>
            <Stack p={3}>
                <Stack direction={'row'} spacing={2} justifyContent={'space-between'} alignItems={'center'}>
                    <InputWithCustomPlaceholder
                        hiddenLabel
                        id="searchKey"
                        value={searchKey ?? ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                            dispatch(setSearchKey(e.target.value));
                        }}
                        placeholder={t('COMMON:SEARCH_PLACEHOLDER')}
                        size="small"
                        InputProps={{
                            startAdornment: <Search id="search" sx={{ mr: '5px', color: '#727e84' }} />,
                        }}
                    />

                    {filters && (
                        <Stack direction="row" spacing={1}>
                            {Object.keys(filters).map(
                                (key: string) =>
                                    key !== 'deviceId' && (
                                        <Chip
                                            key={`unique${key}`}
                                            label={getChipName(key)}
                                            onDelete={(): void => {
                                                handleChipDelete(key);
                                            }}
                                        />
                                    )
                            )}
                        </Stack>
                    )}
                </Stack>
            </Stack>
            <Paper className="box-shadow">
                <CustomTable
                    total={loadsData?.total ?? 10}
                    data={loadsData?.loadConfiguration}
                    ref={tableRef}
                    isPagination
                    // containerClass="custom-data-table"
                    isLoading={isFetching || isLoading}
                    keyToTraverse="id"
                    headers={tableRows}
                    noDataFoundIcon={<LooksOne fontSize="inherit" />}
                    noDataFoundTitle={<CustomTransComponent translationKey={'WIDGETS:LOADS.NO_LOADS_FOUND'} />}
                    noDataFoundButtonText={<CustomTransComponent translationKey={'LOADS:ADD_LOADS'} />}
                    handlePageChange={handlePageChange}
                    handleFilterChange={handleFilterChange}
                    // noDataFoundButtonAction={(): void => navigate(`/${currentRealmName}/addOrEditUser`)}
                />
            </Paper>
        </>
    );
};
